import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Building2,
  Calendar,
  Landmark,
  Compass,
  Palette,
  Sparkles,
} from "lucide-react";
import SEO from "@/components/SEO";
import SectionHeading from "@/components/SectionHeading";
import { api } from "@/lib/api";

const COOP_FIELDS = [
  { icon: Building2, title: "Hotels & Resorts" },
  { icon: Calendar, title: "Eventlocations" },
  { icon: Landmark, title: "Immobilien & Standortentwicklung" },
  { icon: Compass, title: "Tourismusnetzwerke" },
  { icon: Palette, title: "Content Studios" },
  { icon: Sparkles, title: "Markenräume & Showrooms" },
];

export default function Cooperations() {
  const [postings, setPostings] = useState([]);

  useEffect(() => {
    api
      .get("/cooperations/ausschreibungen")
      .then((r) => setPostings(r.data.items || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="PINE Cooperations – Mit PINE Systems arbeiten"
        description="PINE Cooperations verbindet Entwickler, Sales-Partner, Standorte und kreative Köpfe mit PINE Systems und professionellen Erlebnisstandorten."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "PINE Cooperations",
          url: "https://www.pinexs.de/cooperations",
          inLanguage: "de-DE",
        }}
      />

      {/* HERO */}
      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="overline" data-testid="coop-eyebrow">PINE Cooperations</div>
          <h1
            className="font-display text-4xl sm:text-5xl md:text-6xl uppercase mt-4 max-w-5xl leading-[1.04]"
            data-testid="coop-headline"
          >
            Arbeiten Sie mit PINE an der <span className="text-[#18E096]">nächsten Generation</span> von Erlebnisstandorten.
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            PINE Systems verbindet Betreiber, Entwickler, Sales-Partner, Standorte und kreative
            Köpfe zu einem Ökosystem für marktfähige Erlebnisangebote und digitale Markenräume.
          </p>
          <p className="text-[#AEB7B7] mt-4 max-w-3xl text-sm md:text-base leading-relaxed">
            Nicht jede gute Idee wird automatisch ein gutes Standortprodukt. Nicht jeder Kontakt
            wird automatisch ein Projekt. PINE Cooperations schafft klare Zugänge für Menschen und
            Unternehmen, die mit PINE Systems wachsen, Inhalte einbringen oder neue Standorte
            ermöglichen möchten.
          </p>
        </div>
      </section>

      {/* TWO MAIN CARDS */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px border-l border-t border-[rgba(255,255,255,0.08)]">
            <Link
              to="/dev-programm"
              className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 md:p-10 bg-[#0B0F10] hover:bg-[#141819] transition-colors flex flex-col group"
              data-testid="coop-card-dev"
            >
              <div className="overline text-[#18E096]">01 · Dev Programm</div>
              <h2 className="font-display text-2xl md:text-3xl uppercase mt-5 text-white leading-tight">
                Aus guten Games werden marktfähige LBE-Erlebnisse.
              </h2>
              <p className="text-[#AEB7B7] mt-4 leading-relaxed flex-1">
                Für Entwickler, Studios und kreative Teams, die Inhalte nicht nur für private
                Spieler denken, sondern für echte Standorte, Betreiber und Gästegruppen
                marktfähig machen wollen.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-[#18E096] uppercase text-xs tracking-[0.18em]">
                Dev Programm ansehen{" "}
                <ArrowUpRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
            <Link
              to="/sales-partner"
              className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 md:p-10 bg-[#0B0F10] hover:bg-[#141819] transition-colors flex flex-col group"
              data-testid="coop-card-sales"
            >
              <div className="overline text-[#18E096]">02 · Sales Partner</div>
              <h2 className="font-display text-2xl md:text-3xl uppercase mt-5 text-white leading-tight">
                Empfehlen Sie Standorte. Wir bauen daraus Chancen.
              </h2>
              <p className="text-[#AEB7B7] mt-4 leading-relaxed flex-1">
                Für Menschen mit Zugang zu Hotels, Freizeitflächen, Eventlocations, Immobilien,
                Investoren, Tourismus oder Betreiberstrukturen.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-[#18E096] uppercase text-xs tracking-[0.18em]">
                Sales Partner werden{" "}
                <ArrowUpRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* WEITERE KOOPERATIONSFELDER */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="Weitere Kooperationsfelder"
            title="Wo PINE Systems andocken kann."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-14 border-l border-t border-[rgba(255,255,255,0.08)]">
            {COOP_FIELDS.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 hover:bg-[#141819] transition-colors"
                  data-testid={`coop-field-${i}`}
                >
                  <Icon size={24} strokeWidth={1.25} className="text-[#18E096]" />
                  <h3 className="font-display text-lg md:text-xl uppercase mt-5 text-white">
                    {f.title}
                  </h3>
                </div>
              );
            })}
          </div>
          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/kontakt" className="btn-primary" data-testid="coop-cta-kontakt">
              Kooperation anfragen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/standort-pruefen" className="btn-secondary">
              Standortpotenzial prüfen
            </Link>
          </div>
        </div>
      </section>

      {/* AKTUELLE AUSSCHREIBUNGEN */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="Aktuelle Ausschreibungen"
            title="Offene Kooperationszugänge."
          />
          {postings.length === 0 ? (
            <p className="text-[#AEB7B7] mt-8" data-testid="coop-postings-empty">
              Aktuell sind keine öffentlichen Ausschreibungen aktiv. Schauen Sie bald wieder vorbei
              oder schreiben Sie uns über{" "}
              <Link to="/kontakt" className="text-[#18E096] hover:underline">
                Kontakt
              </Link>
              .
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
              {postings.map((p) => (
                <div
                  key={p.id}
                  className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 md:p-10 bg-[#0B0F10]"
                  data-testid={`coop-posting-${p.slug}`}
                >
                  <div className="overline text-[#18E096]">{p.posting_type || "Cooperation"}</div>
                  <h3 className="font-display text-xl md:text-2xl uppercase mt-4 text-white leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-[#AEB7B7] text-sm mt-4 leading-relaxed">
                    {p.short_description}
                  </p>
                  {p.compensation ? (
                    <p className="text-[#F5F7F7] text-sm mt-4 leading-relaxed border-l-2 border-[#18E096] pl-4">
                      {p.compensation}
                    </p>
                  ) : null}
                  {p.cta_url && p.cta_text ? (
                    <div className="mt-6">
                      <Link to={p.cta_url} className="btn-secondary">
                        {p.cta_text} <ArrowUpRight size={16} strokeWidth={1.5} />
                      </Link>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
